import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useSocket } from './kanban/context/SocketContext';
import { useAuth } from 'contexts/AuthContext';
import { useStore } from 'hooks/useStore';
import AvatarGroup from './kanban/shared/AvatarGroup';
import SubtaskIcon from 'assets/img/Subtaskicon.svg';
import FrameIcon from '../../assets/img/HideFrame.svg';
import { createProjectBoardSocket, formatOptions, getPlainText } from 'utils/Helpers';
import AddMilestoneCard from './kanban/Milestone/AddMilestoneCard';

const TaskCalendar = forwardRef(
  (
    {
      calenderViewType,
      onDateChange,
      projectBoardId,
      teams,
      handleMilestoneClick,
      handleSubtaskClick,
      calendarDate,
      searchTerm,
      setCalenderViewType,
      channelOptions,
      isMasterCalender,
      teamMembers,
      priorityOptions,
      reminderOptions,
      isProjectRelatedBoard,
      calendarEvents = [],
      readOnly = false
    },
    ref
  ) => {
    const socket = useSocket();
    const { user } = useAuth();
    const calendarRef = useRef(null);
    const lastJsEventRef = useRef(null);
    const overlayRef = useRef(null);
    const [events, setEvents] = useState([]);
    const [showWeekends, setShowWeekends] = useState(true);
    const [store] = useStore();
    const [timeFormat, setTimeFormat] = useState('');
    const [addMilestoneOverlay, setAddMilestoneOverlay] = useState({
      visible: false,
      date: null, // JS Date object for the clicked slot
      style: null // computed inline style for position
    });
    // persistent refs for click/double-click handling and cleanup
    const lastClickTimeRef = useRef(0);
    const clickTimeoutRef = useRef(null);

    const computeOverlayStyleFromMouse = (jsEvent) => {
      try {
        if (!jsEvent) return null;

        // try several places to find a calendar root element that supports getBoundingClientRect
        const tryCandidates = () => {
          // 1) FullCalendar ref: some FC versions put the element on ref.current.el
          const fcRefEl = calendarRef.current?.el || (calendarRef.current?.getApi && calendarRef.current.getApi().el);
          if (fcRefEl && typeof fcRefEl.getBoundingClientRect === 'function') return fcRefEl;

          // 2) container with your class
          const container = document.querySelector('.calender-body');
          if (container && typeof container.getBoundingClientRect === 'function') return container;

          // 3) any fc root element
          const fcRoot = document.querySelector('.fc');
          if (fcRoot && typeof fcRoot.getBoundingClientRect === 'function') return fcRoot;

          // 4) fallback to documentElement (html) or body if they have method — they don't, so return null
          return null;
        };

        const rootEl = tryCandidates();

        // compute rootRect safely: if we don't have an element, use viewport values
        const rootRect =
          rootEl && typeof rootEl.getBoundingClientRect === 'function'
            ? rootEl.getBoundingClientRect()
            : {
                left: 0,
                top: 0,
                width: window.innerWidth || document.documentElement.clientWidth,
                height: window.innerHeight || document.documentElement.clientHeight
              };

        // find scroller within calendar if possible (keep previous selectors)
        const scroller =
          (rootEl && rootEl.querySelector('.calender-body .fc-scroller')) ||
          document.querySelector('.calender-body .fc-scroller') ||
          document.querySelector('.calender-body .fc-direction-ltr') ||
          document.querySelector('.fc-scroller');

        const scrollerRect = scroller ? scroller.getBoundingClientRect() : null;

        const x = jsEvent.clientX;
        const y = jsEvent.clientY;

        // left: anchor inside scroller if available, otherwise relative to rootRect (viewport fallback)
        const left = scrollerRect ? scrollerRect.left - rootRect.left + 8 : Math.max(8, x - rootRect.left - 50);

        // top relative to root so overlay appears where user clicked (viewport position)
        let top = y - rootRect.top;

        // width: align to scroller width if available
        const availableWidth = scrollerRect ? scrollerRect.width - 16 : Math.max(220, rootRect.width - 16);
        const width = Math.max(220, Math.min(availableWidth, 1000));

        // clamp top so overlay stays inside visible area of the root (or viewport)
        const maxTop = Math.max(0, (rootRect.height || window.innerHeight) - 80);
        top = Math.max(8, Math.min(top, maxTop));

        return {
          position: 'absolute',
          top: `${top - 11}px`,
          // left: `${left}px`,
          // width: `${width}px`,
          zIndex: 9999,
          pointerEvents: 'auto'
        };
      } catch (err) {
        // keep console warning for dev debugging
        console.warn('computeOverlayStyleFromMouse error', err);
        return null;
      }
    };

    // expose API to parent
    useImperativeHandle(ref, () => ({
      goPrev: () => calendarRef.current?.getApi().prev(),
      goNext: () => calendarRef.current?.getApi().next(),
      toggleWeekends: () => setShowWeekends((s) => !s),
      setShowWeekends: (val) => setShowWeekends(!!val)
    }));

    useEffect(() => {
      if (store?.currentTimeFormat) setTimeFormat(store.currentTimeFormat);
    }, [store]);

    // keep calendar date in sync (only gotoDate if date differs)
    useEffect(() => {
      if (!calendarDate) return;
      const api = calendarRef.current?.getApi();
      if (!api) return;
      const current = api.getDate();
      if (!current || current.toDateString() !== new Date(calendarDate).toDateString()) {
        api.gotoDate(calendarDate);
      }
    }, [calendarDate]);

    useEffect(() => {
      setEvents(calendarEvents || []);
    }, [calendarEvents]);

    const resources = [
      { name: 1, id: 1 },
      { name: 2, id: 2 },
      { name: 3, id: 3 },
      { name: 4, id: 4 },
      { name: 5, id: 5 },
      { name: 6, id: 6 },
      { name: 7, id: 7 }
    ];

    // filter by searchTerm
    useEffect(() => {
      if (!searchTerm) {
        setEvents(calendarEvents || []);
      } else {
        const lower = searchTerm.toLowerCase();
        setEvents((calendarEvents || []).filter((ev) => ev.title?.toLowerCase().includes(lower)));
      }
    }, [searchTerm, calendarEvents]);

    // format HH:MM:SS for scrollTime
    const hhmmssFromDate = (d = new Date()) => {
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
      return `${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
    };

    // Close overlay when clicking outside or pressing Escape
    useEffect(() => {
      if (!addMilestoneOverlay.visible) return;

      const onPointerDown = (e) => {
        try {
          // Use composedPath to include portal/poppers outside the overlay subtree
          const path = (e.composedPath && e.composedPath()) || e.path || [];

          // 1) If click is inside our overlay, ignore (do not close)
          if (
            overlayRef.current &&
            path.some(
              (node) =>
                node === overlayRef.current || (node && node.nodeType === 1 && overlayRef.current.contains(node))
            )
          ) {
            return;
          }

          //    This handles popovers rendered in portals as well because we use composedPath.
          if (
            path.some((node) => node && node.nodeType === 1 && node.classList && node.classList.contains('cd-popover'))
          ) {
            return;
          }

          // Otherwise it's an outside click -> close overlay
          setAddMilestoneOverlay({ visible: false, date: null, style: null });
        } catch (err) {
          // Fallback: close overlay if something unexpected happened
          setAddMilestoneOverlay({ visible: false, date: null, style: null });
        }
      };

      const onKeyDown = (e) => {
        if (e.key === 'Escape') {
          setAddMilestoneOverlay({ visible: false, date: null, style: null });
        }
      };

      // Keep capture if you need to detect outside clicks before other handlers.
      window.addEventListener('pointerdown', onPointerDown, true);
      window.addEventListener('keydown', onKeyDown);

      return () => {
        window.removeEventListener('pointerdown', onPointerDown, true);
        window.removeEventListener('keydown', onKeyDown);
      };
    }, [addMilestoneOverlay.visible]);

    // effect to change FullCalendar view when prop changes
    useEffect(() => {
      if (!calenderViewType) return;
      const api = calendarRef.current?.getApi();
      if (!api) {
        // try again next frame
        requestAnimationFrame(() => {
          const retryApi = calendarRef.current?.getApi();
          if (retryApi && retryApi.view.type !== calenderViewType) retryApi.changeView(calenderViewType);
        });
        return;
      }

      // only change view if different
      if (api.view.type !== calenderViewType) {
        api.changeView(calenderViewType);
      }

      // only set view-dependent options when needed
      const isDayOrWeekView = calenderViewType === 'timeGridDay' || calenderViewType === 'timeGridWeek';

      api.setOption('allDaySlot', isDayOrWeekView);
      api.setOption('allDayDefault', isDayOrWeekView);

      if (isDayOrWeekView) {
        // get the view date the calendar is showing
        const viewDate = api.getDate ? api.getDate() : new Date();

        const today = new Date();
        const isSameDay =
          viewDate.getFullYear() === today.getFullYear() &&
          viewDate.getMonth() === today.getMonth() &&
          viewDate.getDate() === today.getDate();

        // Let FullCalendar know the logical scroll time for the view.
        // If it's the same day, use the current time; otherwise default to 07:00.
        api.setOption('scrollTime', isSameDay ? hhmmssFromDate() : '07:00:00');
      }

      // run the (cheap) scroll adjustments after FC finishes rendering
      requestAnimationFrame(() =>
        setTimeout(() => {
          try {
            // lightweight DOM reads/writes (no heavy loops)
            if (isDayOrWeekView) {
              const root = calendarRef.current?.el || document;

              // find the element that actually scrolls
              const findScrollable = (r) => {
                if (!r) return null;
                const selectors = [
                  '.calender-body .fc-scroller-harness-liquid',
                  '.calender-body .fc-scroller-harness',
                  '.calender-body .fc-scroller',
                  '.fc-scroller-harness-liquid',
                  '.fc-scroller-harness',
                  '.fc-scroller'
                ];

                const candidates = [];
                selectors.forEach((sel) => {
                  const el = r.querySelector(sel) || document.querySelector(sel);
                  if (el && !candidates.includes(el)) candidates.push(el);
                });

                // prefer the element that actually can scroll
                for (const el of candidates) {
                  if (el.scrollHeight > el.clientHeight) return el;
                  const childScroller = el.querySelector && el.querySelector('.fc-scroller');
                  if (childScroller && childScroller.scrollHeight > childScroller.clientHeight) return childScroller;
                }

                // fallback to any fc-scroller in document or first candidate
                return document.querySelector('.fc-scroller') || candidates[0] || null;
              };

              const scroller = findScrollable(root);
              if (!scroller) return;

              const viewDate = calendarRef.current?.getApi()?.getDate?.() || new Date();
              const today = new Date();
              const isSameDay =
                viewDate &&
                viewDate.getFullYear() === today.getFullYear() &&
                viewDate.getMonth() === today.getMonth() &&
                viewDate.getDate() === today.getDate();

              let minutesIntoDay;
              if (isSameDay) {
                // Use the real current time (so the calendar scrolls to now).
                const now = new Date();
                minutesIntoDay = Math.max(0, now.getHours() * 60 + now.getMinutes());

                // Optional: show a bit of context above the "now" line so it's not flush to the top.
                // The `centerOffsetRatio` controls how far from the top of the scroller the current time sits.
                // 0.25 means current time sits 25% down from the top (tweak to taste: 0.2 - 0.35 are common).
                const centerOffsetRatio = 0.5;

                const slotEl =
                  document.querySelector('.fc-timegrid-slot') || document.querySelector('.fc-timegrid-slot-label');
                const slotHeight = slotEl ? slotEl.offsetHeight : 50;
                const slotDurationMinutes = 22;

                const top = Math.max(
                  0,
                  Math.round(
                    (minutesIntoDay / slotDurationMinutes) * slotHeight - scroller.clientHeight * centerOffsetRatio
                  )
                );

                if (typeof scroller.scrollTo === 'function') {
                  scroller.scrollTo({ top, behavior: 'auto' });
                } else {
                  scroller.scrollTop = top;
                }

                // we already handled scrolling here, so return to avoid the fallback below
                return;
              } else {
                minutesIntoDay = 10 * 60;
              }

              const slotEl =
                document.querySelector('.fc-timegrid-slot') || document.querySelector('.fc-timegrid-slot-label');
              const slotHeight = slotEl ? slotEl.offsetHeight : 50;
              const slotDurationMinutes = 30;
              const top = Math.max(
                0,
                Math.round((minutesIntoDay / slotDurationMinutes) * slotHeight - scroller.clientHeight * 0.25)
              );

              // set scroll on the actual scrollable element
              if (typeof scroller.scrollTo === 'function') {
                scroller.scrollTo({ top, behavior: 'auto' });
              } else {
                scroller.scrollTop = top;
              }
            } else {
              // for month/dayGrid views reset scroll
              const scroller =
                document.querySelector('.calender-body .fc-scroller') ||
                document.querySelector('.calender-body .fc-direction-ltr');
              if (scroller) {
                if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top: 0, behavior: 'auto' });
                else scroller.scrollTop = 0;
              }
            }
          } catch (err) {
            // swallow DOM-measure errors
            // console.warn(err);
          }
        }, 120)
      );
    }, [calenderViewType, timeFormat]);

    // cleanup timers/listeners on unmount
    useEffect(() => {
      return () => {
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
          clickTimeoutRef.current = null;
        }
      };
    }, []);

    const handleEventDrop = (info) => {
      if (readOnly) {
        info.revert();
        return;
      }

      const { event } = info;
      const newDate = event.startStr;
      const type = event.extendedProps.type;

      if (type === 'subtask') {
        handleDueDate(
          newDate,
          'subtask',
          event.id,
          event.extendedProps.milestoneId,
          event.extendedProps.parentProjectBoardId
        );
      } else if (type === 'milestone') {
        handleDueDate(newDate, 'milestone', event.id, event.id, event.extendedProps.parentProjectBoardId);
      } else if (type === 'checklist' || type === 'subtask-checklist') {
        handleDueDate(
          newDate,
          type,
          event.id,
          event.extendedProps.milestoneId,
          event.extendedProps.parentProjectBoardId,
          event.extendedProps.subtaskId
        );
      }

      setEvents((prev) => prev.map((e) => (e.id === event.id ? { ...e, start: newDate } : e)));
    };

    const handleDueDate = (date, type, id, milestoneId, parentProjectBoardId = '', subtaskId = '') => {
      let masterSocket = socket;
      let selectedBoardId = projectBoardId;
      if (isMasterCalender) {
        masterSocket = createProjectBoardSocket(user._id, parentProjectBoardId);
        selectedBoardId = parentProjectBoardId;
      }
      const validDate = date instanceof Date ? date : new Date(date);
      const dueDateString = validDate.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: false
      });

      if (type === 'subtask') {
        masterSocket?.emit(
          'update-subtask',
          { id, milestoneId, payload: { dueDate: validDate, dueDateString } },
          selectedBoardId
        );
      } else if (type === 'milestone') {
        masterSocket?.emit('update-milestone', {
          projectBoardId: selectedBoardId,
          id,
          payload: { kbDueDate: validDate, kbDueDateString: dueDateString }
        });
      } else if (type === 'checklist' || type === 'subtask-checklist') {
        masterSocket?.emit(
          'update-checklist',
          {
            id,
            milestoneId,
            ...(type === 'subtask-checklist' && subtaskId ? { subtaskId } : {}),
            payload: { dueDate: validDate, dueDateString }
          },
          selectedBoardId
        );
      }
    };

    // double-click and dateClick handlers use refs so they persist across renders
    const handleDateClick = (info) => {
      const now = Date.now();
      const api = calendarRef.current?.getApi();
      if (info.allDay) {
        return;
      }
      if (isMasterCalender && calenderViewType === 'timeGridDay') {
        return;
      }
      if (now - lastClickTimeRef.current < 300) {
        // DOUBLE CLICK
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
          clickTimeoutRef.current = null;
        }
        lastClickTimeRef.current = 0;

        if (calenderViewType === 'timeGridDay') {
          const clickedDate = info.date instanceof Date ? info.date : new Date(info.date);
          // save last event for recompute on scroll/resize
          lastJsEventRef.current = info.jsEvent || null;
          const style = computeOverlayStyleFromMouse(info.jsEvent || null);
          // console.log(clickedDate, 'clickedDate');

          setAddMilestoneOverlay({ visible: true, date: clickedDate, style });
        } else {
          // Switch view AND navigate to that day
          requestAnimationFrame(() => {
            api.gotoDate(info.date);
            setCalenderViewType('timeGridDay');
          });
        }
      } else {
        // SINGLE CLICK
        lastClickTimeRef.current = now;
        if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = setTimeout(() => {
          lastClickTimeRef.current = 0;
          clickTimeoutRef.current = null;
        }, 300);
      }
    };

    return (
      <div
        style={{ maxWidth: '900px', margin: 'auto' }}
        className={`calender-body ${calenderViewType === 'dayGridMonth' ? 'monthview' : ''} ${calenderViewType === 'timeGridDay' ? 'dayview' : ''} ${
          calenderViewType === 'timeGridWeek' ? 'weekview' : ''
        }`}>
        {calenderViewType === 'timeGridWeek' && (
          <img
            src={FrameIcon}
            alt="Calender"
            className="cursor_pointer calender-frame-icon"
            style={{ transform: showWeekends ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }}
            onClick={() => setShowWeekends((s) => !s)}
          />
        )}

        <FullCalendar
          // key={calenderViewType === 'dayGridMonth' ? JSON.stringify(events.map((e) => e.id)) : 'stable-view'}
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          headerToolbar={false}
          initialView="dayGridMonth"
          editable={!readOnly}
          droppable={!readOnly}
          events={events}
          eventDrop={handleEventDrop}
          // replace the current datesSet prop with this function
          datesSet={(arg) => {
            const api = calendarRef.current?.getApi();
            const currentDate = api?.getDate();

            let title = arg.view.title; // fallback

            try {
              if (arg.view.type === 'timeGridDay') {
                title = arg.start.toLocaleDateString('en-US', {
                  weekday: 'short', // Thu instead of Thursday
                  month: 'short', // Feb instead of February
                  day: 'numeric'
                });
              } else if (arg.view.type === 'timeGridWeek') {
                // Compute start & end of the visible week range:
                const weekStart = arg.start;
                const weekEnd = new Date(weekStart);
                // make sure end is the last visible day of the week (6 days after start)
                weekEnd.setDate(weekStart.getDate() + 6);

                // Format like "November 23 - November 29"
                const startStr = weekStart.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                });

                const endStr = weekEnd.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                });

                title = `${startStr} - ${endStr}`;
              }
            } catch (e) {
              // fallback to arg.view.title if anything goes wrong
              title = arg.view.title;
            }

            onDateChange(title, currentDate);
          }}
          eventDisplay="block"
          // slotDuration="00:15:00"
          // slotLabelInterval="00:30:00"
          slotEventOverlap={false}
          eventMaxStack={100}
          eventOverlap={false}
          allDaySlot={calenderViewType === 'timeGridDay' || calenderViewType === 'timeGridWeek'}
          allDayDefault={calenderViewType === 'timeGridDay' || calenderViewType === 'timeGridWeek'}
          nowIndicator
          displayEventEnd
          timeZone="local"
          eventOrder={(a, b) => {
            const da = a.start ? new Date(a.start).getTime() : Infinity;
            const db = b.start ? new Date(b.start).getTime() : Infinity;
            return da - db;
          }}
          dayMaxEvents={calenderViewType === 'timeGridDay' ? 6 : calenderViewType === 'timeGridWeek' ? 1 : 3}
          dayMaxEventRows={3} // keep your limit
          moreLinkClick={(arg) => {
            const cell = arg.jsEvent?.target?.closest('.fc-daygrid-day');
            if (!cell) return 'none';

            const row = cell.closest('tr');
            if (!row) return 'none';

            // remove expansion from other rows (optional)
            document.querySelectorAll('tr.fc-expanded').forEach((r) => {
              if (r !== row) {
                r.classList.remove('fc-expanded');
                r.style.height = '';
              }
            });

            // reveal hidden events first
            row.querySelectorAll('.fc-daygrid-event-harness-abs').forEach((ev) => {
              ev.style.visibility = 'visible';
              ev.style.position = 'relative';
            });

            // measure the tallest day in this week
            let maxHeight = 0;

            row.querySelectorAll('.fc-daygrid-day-events').forEach((dayEvents) => {
              const h = dayEvents.scrollHeight;
              if (h > maxHeight) maxHeight = h;
            });

            // add some breathing room
            const padding = 12;

            row.style.height = `${maxHeight + padding + 10}px`;
            row.classList.add('fc-expanded');

            return 'none'; // disable popover
          }}
          expandRows={true}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: timeFormat === '12hour'
          }}
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: timeFormat === '12hour'
          }}
          nowIndicatorDidMount={({ el }) => {
            const updateTime = () => {
              const now = new Date();
              el.setAttribute('data-time', now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
            };

            updateTime();
            const id = setInterval(updateTime, 60000);

            return () => clearInterval(id); // ✅ cleanup here
          }}
          nowIndicatorDidUnmount={({ el }) => {
            const id = el && el.__nowIndicatorIntervalId;
            if (id) clearInterval(id);
          }}
          weekends={showWeekends}
          titleFormat={{ month: 'short', year: 'numeric' }}
          slotLaneContent={(args) => (
            <div style={{ display: 'flex', width: '100%' }}>
              {resources.map((resource) => (
                <div className="slot-cell" key={resource.id}>
                  <span>{args.date?.toLocaleTimeString(undefined, { timeStyle: 'short' })}</span>
                </div>
              ))}
            </div>
          )}
          dayCellDidMount={(info) => {
            // add dblclick listener but clean it up in the element's cleanup hook
            const onDbl = (e) => {
              e.preventDefault();
              const api = calendarRef.current?.getApi();
              const date = info.date; // ⬅ FullCalendar gives you this
              requestAnimationFrame(() => {
                api.gotoDate(date);
                setCalenderViewType('timeGridDay');
              });
            };
            info.el.addEventListener('dblclick', onDbl);
            info.el.__dblHandler = onDbl;
            info.el.cleanup = () => {
              if (info.el.__dblHandler) {
                info.el.removeEventListener('dblclick', info.el.__dblHandler);
                info.el.__dblHandler = null;
              }
            };
          }}
          dateClick={handleDateClick}
          dayHeaderDidMount={(info) => {
            if (info.view.type === 'timeGridWeek') {
              const onDblClick = (e) => {
                e.preventDefault();
                requestAnimationFrame(() => setCalenderViewType('timeGridDay'));
              };
              info.el.addEventListener('dblclick', onDblClick);
              info.el.__headerDbl = onDblClick;
              info.el.cleanup = () => {
                if (info.el.__headerDbl) {
                  info.el.removeEventListener('dblclick', info.el.__headerDbl);
                  info.el.__headerDbl = null;
                }
              };
            }
          }}
          dayHeaderContent={(args) => {
            if (args.view.type === 'timeGridWeek') {
              const date = args.date.getDate();
              const weekday = args.date.toLocaleDateString('en-US', { weekday: 'long' });
              return (
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '21px', fontWeight: '600' }}>{date}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>{weekday}</div>
                </div>
              );
            } else if (args.view.type === 'timeGridDay') {
              const date = args.date.getDate();
              const weekday = args.date.toLocaleDateString('en-US', { weekday: 'long' });
              return (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '21px', fontWeight: '600', textAlign: 'left' }}>{date}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>{weekday}</div>
                </div>
              );
            }
            return args.text;
          }}
          eventContent={(arg) => {
            const type = arg.event.extendedProps.type;
            if (type === 'checklist' || type === 'subtask-checklist') {
              const isChecked = arg.event.extendedProps.isCompleted || false;
              return (
                <div style={{ width: '100%' }}>
                  <div className="checklist-item-calender">
                    <div
                      className={
                        'checklist-event tooltipWrapper fc-event-inner ' +
                        (arg.event.extendedProps.assigneeIds.length == 0 ? 'w-100' : '')
                      }
                      onClick={() => {
                        type === 'subtask-checklist'
                          ? handleSubtaskClick(arg.event.extendedProps, true)
                          : handleMilestoneClick(arg.event.extendedProps, true);
                      }}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={readOnly}
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginRight: '4px' }}
                      />
                      <label
                        aria-disabled={readOnly}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (readOnly) return;

                          let masterSocket = socket;
                          let selectedBoardId = projectBoardId;
                          if (isMasterCalender) {
                            masterSocket = createProjectBoardSocket(
                              user._id,
                              arg?.event?.extendedProps?.parentProjectBoardId
                            );
                            selectedBoardId = arg?.event?.extendedProps?.parentProjectBoardId;
                          }
                          setEvents((prev) =>
                            prev.map((event) =>
                              event.id === arg.event.id
                                ? {
                                    ...event,
                                    isCompleted: !isChecked
                                  }
                                : event
                            )
                          );
                          const payload = { isCompleted: !isChecked };
                          if (type === 'subtask-checklist') {
                            masterSocket?.emit(
                              'update-checklist',
                              {
                                id: arg.event.id,
                                subtaskId: arg?.event?.extendedProps?.subtaskId,
                                milestoneId: arg?.event?.extendedProps?.milestoneId,
                                payload
                              },
                              selectedBoardId
                            );
                          } else {
                            masterSocket?.emit(
                              'update-checklist',
                              {
                                id: arg.event.id,
                                milestoneId: arg?.event?.extendedProps?.milestoneId,
                                payload
                              },
                              selectedBoardId
                            );
                          }
                        }}
                        className={`checkbox2 ${isChecked ? 'checked' : ''}`}
                        style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}>
                        &nbsp;
                      </label>

                      <Tippy
                        content={
                          isMasterCalender
                            ? `${arg?.event?.extendedProps?.projectName} / ${
                                arg?.event?.extendedProps?.milestoneName
                              } / ${getPlainText(arg.event.title)}`
                            : `${arg?.event?.extendedProps?.milestoneName} / ${getPlainText(arg.event.title)}`
                        }
                        placement="top"
                        arrow={true}>
                        <span style={{ width: '100%' }} className={isChecked ? `checked` : ''}>
                          {getPlainText(arg.event.title)}
                        </span>
                      </Tippy>
                    </div>
                    {/* <AvatarGroup
                      members={!isMasterCalender ? teams : arg.event.extendedProps?.teams}
                      size={24}
                      limit={1}
                      membersList={arg.event?.extendedProps?.assigneeIds}
                      isCalender={true}
                    /> */}
                  </div>
                </div>
              );
            }

            if (type === 'milestone') {
              const isChecked = arg.event.extendedProps.isCompleted || false;

              return (
                <div style={{ width: '100%' }}>
                  <div className="checklist-item-calender">
                    <div
                      className={`milestone-event tooltipWrapper fc-event-inner ${
                        arg.event.extendedProps.assigneeIds.length === 0 ? 'w-100' : ''
                      }`}
                      onClick={() => handleMilestoneClick(arg.event.extendedProps)}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={readOnly}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <label
                        aria-disabled={readOnly}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (readOnly) return;

                          let masterSocket = socket;
                          let selectedBoardId = projectBoardId;

                          if (isMasterCalender) {
                            masterSocket = createProjectBoardSocket(
                              user._id,
                              arg.event.extendedProps.parentProjectBoardId
                            );
                            selectedBoardId = arg.event.extendedProps.parentProjectBoardId;
                          }

                          const payload = { isCompleted: !isChecked };

                          masterSocket?.emit('update-milestone', {
                            projectBoardId: selectedBoardId,
                            id: arg.event.id,
                            payload
                          });

                          // local optimistic update
                          setEvents((prev) =>
                            prev.map((ev) =>
                              ev.id === arg.event.id
                                ? {
                                    ...ev,
                                    extendedProps: {
                                      ...ev.extendedProps,
                                      isCompleted: !isChecked
                                    }
                                  }
                                : ev
                            )
                          );
                        }}
                        className={`checkbox2 ${isChecked ? 'checked' : ''}`}
                        style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}>
                        &nbsp;
                      </label>
                      <Tippy
                        content={
                          isMasterCalender
                            ? `${arg?.event?.extendedProps?.projectName} / ${arg.event.title}`
                            : getPlainText(arg.event.title)
                        }
                        placement="top"
                        arrow>
                        <span className={isChecked ? 'checked' : ''}>{getPlainText(arg.event.title)}</span>
                      </Tippy>
                    </div>

                    {/* <AvatarGroup
                      members={!isMasterCalender ? teams : arg.event.extendedProps?.teams}
                      size={24}
                      limit={1}
                      membersList={arg.event.extendedProps.assigneeIds}
                      isCalender
                    /> */}
                  </div>
                </div>
              );
            }

            if (type === 'subtask') {
              const status = arg.event.extendedProps.status;
              let textDecoration = 'none';
              if (status === '687940ccdd4622df947a032b') {
                textDecoration = 'line-through';
              }
              return (
                <>
                  <div
                    className={`subtask-event tooltipWrapper fc-event-inner ${
                      arg.event.extendedProps.assigneeIds.length === 0 ? 'w-100' : ''
                    }`}
                    onClick={() => handleSubtaskClick(arg.event.extendedProps)}>
                    <img src={SubtaskIcon} alt="hello-icon" className="subtask-icon-calender" />
                    <Tippy
                      content={
                        isMasterCalender
                          ? `${arg?.event?.extendedProps?.projectName} / ${
                              arg?.event?.extendedProps?.milestoneName
                            } / ${getPlainText(arg.event.title)}`
                          : `${arg?.event?.extendedProps?.milestoneName} / ${getPlainText(arg.event.title)}`
                      }
                      placement="top"
                      arrow={true}>
                      <span style={{ marginRight: '6px', width: '100%', textDecoration }}>
                        {getPlainText(arg.event.title)}
                      </span>
                    </Tippy>
                  </div>
                  {/* <AvatarGroup
                    members={!isMasterCalender ? teams : arg.event.extendedProps?.teams}
                    size={24}
                    limit={1}
                    membersList={arg.event.extendedProps.assigneeIds}
                    isCalender={true}
                  /> */}
                </>
              );
            }

            // fallback
            return { html: `<span>${arg.event.title}</span>` };
          }}
        />
        {!isMasterCalender && addMilestoneOverlay.visible && calenderViewType === 'timeGridDay' && (
          <div
            ref={overlayRef}
            className="fc-add-milestone-overlay"
            style={addMilestoneOverlay.style}
            // stop propagation so clicking the overlay doesn't toggle calendar handlers
            onClick={(e) => e.stopPropagation()}>
            <div className="fc-add-milestone-inner">
              {/* <div className="add-milestone-label">Add Milestone</div> */}

              <div className="add-milestone-actions">
                {/* Replace these handlers with the function that opens your actual add-milestone UI */}
                <AddMilestoneCard
                  projectBoardId={projectBoardId}
                  // statusId={statusId}
                  // statusName={statusName}
                  members={teamMembers}
                  priorityOptions={priorityOptions}
                  // setDetailsBox={setDetai/lsBox}
                  scrollToBottom={() => {}}
                  reminderOptions={reminderOptions}
                  isfromCalender={true}
                  statusOptions={channelOptions}
                  isProjectRelatedBoard={isProjectRelatedBoard}
                  defaultDueDate={addMilestoneOverlay?.date}
                  onSaved={() => {
                    // close overlay in parent
                    setAddMilestoneOverlay({ visible: false, date: null, style: null });
                    // also clear last event ref if you stored it earlier
                    lastJsEventRef.current = null;
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default TaskCalendar;
